import * as Sentry from "@sentry/browser";
import React from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import App, { AdminContext } from "./App";
import AdminGuard from "./components/AdminGuard";
import InternalGuard from "./components/internal/InternalGuard";
import "@shared/ui/src/fonts/fonts.css";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import Analytics from "./routes/Analytics";
import Carriers from "./routes/Carriers";
import IgnitionSessionDetails from "./routes/IgnitionSessionDetails";
import IgnitionSessions from "./routes/IgnitionSessions";
import InviteTeamMember from "./routes/InviteTeamMember";
import NewIgnitionSession from "./routes/NewIgnitionSession";
import Policies from "./routes/Policies";
import PolicyDetails from "./routes/PolicyDetails";
import Settings from "./routes/Settings";
import Team from "./routes/Team";
import AxleAi from "./routes/internal/AxleAi";
import ClientConfigComparison from "./routes/internal/ClientConfigComparison";
import ClientSearch from "./routes/internal/ClientSearch";

// Custom component to handle root path redirect while preserving query parameters
const RootRedirect = () => {
  const location = useLocation();
  const userRole = React.useContext(AdminContext);

  // Redirect platform-analyst to analytics since they don't have access to ignition
  const defaultPath =
    userRole === "platform-analyst" ? "/analytics" : "/ignition";
  return <Navigate to={`${defaultPath}${location.search}`} replace />;
};

Sentry.init({
  environment: process.env.REACT_APP_STAGE,
  dsn: `https://${process.env.REACT_APP_SENTRY_IO_DSN_HASH}.ingest.us.sentry.io/${process.env.REACT_APP_SENTRY_IO_PROJECT_ID}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
    Sentry.consoleLoggingIntegration({ levels: ["error"] }),
    // Sentry.replayIntegration(), // Only available in Business Plan
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ["localhost"],
  replaysSessionSampleRate: 0.5,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
});

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root element with id 'root' was not found.");
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<App />}>
          <Route path="ignition">
            <Route index element={<IgnitionSessions />} />
            <Route path="new" element={<NewIgnitionSession />} />
            <Route path=":ignitionToken" element={<IgnitionSessionDetails />} />
          </Route>
          <Route path="policies">
            <Route index element={<Policies />} />
            <Route path=":policyId" element={<PolicyDetails />} />
          </Route>

          <Route path="" element={<RootRedirect />} />
          <Route path="carriers">
            <Route index element={<Carriers />} />
          </Route>
          <Route path="team">
            <Route index element={<Team />} />
            <Route path="invite" element={<InviteTeamMember />} />
          </Route>
          <Route path="analytics">
            <Route index element={<Analytics />} />
          </Route>

          {/* Settings — gated by AdminGuard (admin / platform-admin only) */}
          <Route path="settings" element={<AdminGuard />}>
            <Route index element={<Settings />} />
          </Route>

          {/* Client Config Comparison Tool — gated by InternalGuard (internal-axle Cognito group) */}
          <Route path="internal" element={<InternalGuard />}>
            <Route path="clients" element={<ClientSearch />} />
            <Route
              path="clients/:platformClientId/compare"
              element={<ClientConfigComparison />}
            />
            <Route path="ai" element={<AxleAi />} />
            <Route index element={<Navigate to="clients" replace />} />
          </Route>

          {/* Catch-all route - redirect any undefined paths to ignition page */}
          <Route path="*" element={<Navigate to="/ignition" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
